// ===== ESTIMATE STATUS FLOW =====
// draft → sent → approved/declined/expired → (if approved) converted

// ===== LEAD STATUS FLOW =====
// new → contacted → walkthrough_scheduled → estimate_in_progress → estimate_sent → follow_up → won / lost

export const leadStatuses = {
  new: { label: 'New Lead', color: '#d97706', bg: '#fef3c7', description: 'Just submitted, needs review' },
  contacted: { label: 'Contacted', color: '#8b5cf6', bg: '#f5f3ff', description: 'Initial contact made' },
  walkthrough_scheduled: { label: 'Walkthrough', color: '#0891b2', bg: '#ecfeff', description: 'On-site walkthrough scheduled' },
  estimate_in_progress: { label: 'Estimating', color: '#ea580c', bg: '#fff7ed', description: 'Building the estimate' },
  estimate_sent: { label: 'Estimate Sent', color: '#b8926a', bg: '#fdf6ed', description: 'Estimate sent, awaiting response' },
  follow_up: { label: 'Follow Up', color: '#2563eb', bg: '#eff6ff', description: 'Needs follow-up, no response yet' },
  won: { label: 'Won', color: '#16a34a', bg: '#f0fdf4', description: 'Client approved, converted to job' },
  lost: { label: 'Lost', color: '#dc2626', bg: '#fef2f2', description: 'Did not convert' },
};

export const closeLostReasons = [
  { value: 'too_expensive', label: 'Too Expensive' },
  { value: 'went_with_competitor', label: 'Went With Competitor' },
  { value: 'bad_timing', label: 'Bad Timing' },
  { value: 'no_response', label: 'No Response' },
  { value: 'not_a_fit', label: 'Not a Fit' },
  { value: 'other', label: 'Other' },
];

export const leadStatusFlow = [
  'new', 'contacted', 'walkthrough_scheduled', 'estimate_in_progress', 'estimate_sent', 'follow_up', 'won'
];

export const estimateStatuses = {
  pending_review: {
    label: 'Needs Review',
    color: '#d97706',
    bgColor: '#fef3c7',
    description: 'Submitted online, needs manager review'
  },
  draft: { 
    label: 'Draft', 
    color: 'var(--text-muted)',
    bgColor: 'var(--cream-dark)',
    description: 'Not yet sent to client'
  },
  sent: { 
    label: 'Sent', 
    color: 'var(--gold)',
    bgColor: 'var(--gold-pale)',
    description: 'Awaiting client response'
  },
  approved: { 
    label: 'Approved', 
    color: 'var(--green)',
    bgColor: 'var(--green-light)',
    description: 'Client approved, ready to schedule'
  },
  declined: { 
    label: 'Declined', 
    color: 'var(--red)',
    bgColor: 'var(--red-light)',
    description: 'Client declined estimate'
  },
  expired: { 
    label: 'Expired', 
    color: 'var(--text-muted)',
    bgColor: 'var(--cream-dark)',
    description: 'No response within validity period'
  },
  converted: { 
    label: 'Converted', 
    color: 'var(--sage)',
    bgColor: 'var(--sage-pale)',
    description: 'Converted to scheduled job'
  }
};

export const homeConditions = {
  super_clean: {
    label: 'Super Clean Freaks',
    shortLabel: 'Super Clean',
    description: 'Immaculate home, minimal cleaning needed',
    icon: '✨'
  },
  second_home: {
    label: 'Clean (Second Home)',
    shortLabel: 'Second Home',
    description: 'Rarely used, stays tidy',
    icon: '🏠'
  },
  lived_in: {
    label: 'Clean but Lived In',
    shortLabel: 'Lived In',
    description: 'Normal family home, regular use',
    icon: '👨‍👩‍👧'
  },
  busy_home: {
    label: "There's a Lot of Us",
    shortLabel: 'Busy Home',
    description: 'Busy household, lots of activity',
    icon: '👨‍👩‍👧‍👦'
  },
  overwhelmed: {
    label: "I'm Overwhelmed",
    shortLabel: 'Overwhelmed',
    description: 'Needs extra attention and care',
    icon: '🆘'
  }
};

export const cleaningFrequencies = {
  weekly: { label: 'Weekly', sortOrder: 1 },
  biweekly: { label: 'Bi-Weekly', sortOrder: 2 },
  monthly: { label: 'Monthly', sortOrder: 3 },
  one_time: { label: 'One-Time Deep Clean', sortOrder: 4 },
  move_in_out: { label: 'Move In/Move Out', sortOrder: 5 },
  airbnb: { label: 'Short-Term Rental', sortOrder: 6, requiresCustomQuote: true }
};

// Default validity period for estimates (days)
export const ESTIMATE_VALIDITY_DAYS = 14;

// ===== JOB STATUS FLOW =====
// scheduled → in_progress → completed → invoiced

export const jobStatuses = {
  scheduled: {
    label: 'Scheduled',
    color: 'var(--gold)',
    bgColor: 'var(--gold-pale)'
  },
  in_progress: {
    label: 'In Progress',
    color: 'var(--sage)',
    bgColor: 'var(--sage-pale)'
  },
  completed: {
    label: 'Completed',
    color: 'var(--green)',
    bgColor: 'var(--green-light)'
  },
  invoiced: {
    label: 'Invoiced',
    color: 'var(--teal)',
    bgColor: 'rgba(61, 107, 107, 0.1)'
  },
  cancelled: {
    label: 'Cancelled',
    color: 'var(--red)',
    bgColor: 'var(--red-light)'
  }
};

// Sample jobs (converted from estimates)
export const sampleJobs = [];

// ===== INVOICE STATUS =====
export const invoiceStatuses = {
  draft: { label: 'Draft', color: 'var(--text-muted)' },
  sent: { label: 'Sent', color: 'var(--gold)' },
  paid: { label: 'Paid', color: 'var(--green)' },
  overdue: { label: 'Overdue', color: 'var(--red)' },
  cancelled: { label: 'Cancelled', color: 'var(--text-muted)' }
};

export const sampleInvoices = [];

// ===== STANDARD SERVICE SCOPE =====
// Defines what is included in each service type so customers can see exactly
// what to expect. Any variances from this standard scope will be noted on the
// individual estimate/quote.

export const standardServiceScope = {
  recurring: {
    label: 'Recurring Cleaning',
    subtitle: 'Weekly, Bi-Weekly & Monthly Services',
    rooms: {
      kitchen: {
        label: 'Kitchen',
        icon: '🍳',
        items: [
          'Wipe down countertops and backsplash',
          'Clean exterior of all appliances (stove, microwave, dishwasher, fridge)',
          'Clean inside microwave',
          'Clean and sanitize sink and fixtures',
          'Wipe cabinet fronts (as needed)',
          'Clean stovetop surface',
          'Wipe down small appliances on counters',
          'Empty trash and replace liner',
          'Sweep and mop floors',
          'Wipe light switches and door handles'
        ]
      },
      bathrooms: {
        label: 'Bathrooms',
        icon: '🚿',
        items: [
          'Clean and sanitize toilet (inside and out)',
          'Clean sink, countertop, and fixtures',
          'Clean and polish mirrors',
          'Clean shower/tub surfaces',
          'Wipe cabinet fronts',
          'Empty trash and replace liner',
          'Sweep and mop floors',
          'Wipe light switches and door handles'
        ]
      },
      bedrooms: {
        label: 'Bedrooms',
        icon: '🛏️',
        items: [
          'Make beds (change linens if left out)',
          'Dust all reachable surfaces',
          'Dust nightstands, dressers, and shelves',
          'Vacuum floors and area rugs',
          'Wipe light switches and door handles'
        ]
      },
      living_areas: {
        label: 'Living Areas',
        icon: '🛋️',
        items: [
          'Dust all reachable surfaces',
          'Dust furniture, shelves, and decor',
          'Vacuum upholstered furniture (as needed)',
          'Vacuum floors and area rugs',
          'Wipe light switches and door handles',
          'General straightening and tidying'
        ]
      },
      general: {
        label: 'Throughout the Home',
        icon: '🏠',
        items: [
          'Dust ceiling fans (within reach)',
          { text: 'Light dusting of blinds', warning: 'Blinds exposed to prolonged sun can become brittle and may break during cleaning. We are not responsible for damage to sun-damaged blinds.' },
          'Vacuum all carpeted areas',
          'Sweep and mop all hard floors',
          'Remove cobwebs (within reach)',
          'Wipe baseboards (on rotation as needed)'
        ]
      }
    },
    notIncluded: [
      'Inside oven or fridge (available as add-on)',
      'Interior windows (available as add-on)',
      'Window tracks or blinds deep clean (available as add-on)',
      'Inside cabinets or drawers',
      'Laundry or dishes',
      'Exterior cleaning',
      'Organizing or decluttering (available as add-on)',
      'Moving heavy furniture',
      'Taking down books or removing items from shelving',
      'Surfaces not reachable with a 6-foot duster or 3-step ladder',
      'Animal urine, feces, or human waste (may incur additional costs)',
      'Smoke residue or nicotine buildup (may incur additional costs)',
      'Hazardous materials, needles, or biohazards',
      'Homes without running water, electricity, or HVAC between 64°F–75°F'
    ]
  },
  deep_clean: {
    label: 'Deep Clean',
    subtitle: 'One-Time Deep Cleans & First-Visit Cleans',
    rooms: {
      kitchen: {
        label: 'Kitchen',
        icon: '🍳',
        items: [
          'Everything in a standard recurring clean, PLUS:',
          'Degrease stovetop, drip pans, and range hood exterior',
          'Thoroughly wipe down all cabinet fronts',
          'Clean and detail baseboards',
          'Detail light fixtures and vents',
          'Scrub backsplash and tile'
        ]
      },
      bathrooms: {
        label: 'Bathrooms',
        icon: '🚿',
        items: [
          'Everything in a standard recurring clean, PLUS:',
          'Scrub tile and grout',
          'Detail around fixtures and caulking',
          'Clean and detail baseboards',
          'Scrub shower doors and tracks',
          'Detail vents and light fixtures',
          'Remove soap scum and hard water buildup'
        ]
      },
      bedrooms: {
        label: 'Bedrooms',
        icon: '🛏️',
        items: [
          'Everything in a standard recurring clean, PLUS:',
          'Detailed dusting of all surfaces, shelves, and decor',
          'Clean baseboards throughout',
          'Dust and wipe window sills',
          'Vacuum under beds (if accessible)',
          'Detail accessible closet shelves'
        ]
      },
      living_areas: {
        label: 'Living Areas',
        icon: '🛋️',
        items: [
          'Everything in a standard recurring clean, PLUS:',
          'Detailed dusting of all decor, shelving, and frames',
          'Clean baseboards throughout',
          'Dust and wipe window sills',
          'Vacuum under/behind furniture (if movable)',
          'Detail ceiling fan blades',
          'Clean vents and returns'
        ]
      },
      general: {
        label: 'Throughout the Home',
        icon: '🏠',
        items: [
          'Hand-wipe all baseboards',
          'Dust and wipe all door frames',
          'Clean all window sills',
          'Wipe all light switch plates and outlet covers',
          'Detail all vents and returns',
          'Clean interior doors',
          'Wipe all door handles and hardware'
        ]
      }
    },
    notIncluded: [
      'Inside oven (available as add-on)',
      'Inside fridge (available as add-on)',
      'Interior windows (available as add-on)',
      'Inside cabinets or drawers',
      'Laundry or dishes',
      'Exterior cleaning',
      'Grout restoration (available as custom add-on)',
      'Moving heavy furniture',
      'Taking down books or removing items from shelving',
      'Surfaces not reachable with a 6-foot duster or 3-step ladder',
      'Animal urine, feces, or human waste (may incur additional costs)',
      'Smoke residue or nicotine buildup (may incur additional costs)',
      'Hazardous materials, needles, or biohazards',
      'Homes without running water, electricity, or HVAC between 64°F–75°F'
    ]
  },
  move_in_out: {
    label: 'Move In / Move Out',
    subtitle: 'Comprehensive Cleaning for Move Transitions',
    rooms: {
      kitchen: {
        label: 'Kitchen',
        icon: '🍳',
        items: [
          'Everything in a deep clean, PLUS:',
          'Clean inside all cabinets and drawers',
          'Clean inside oven (standard level)',
          'Clean inside refrigerator (if empty)',
          'Clean inside dishwasher',
          'Clean pantry shelves',
          'Degrease range hood and filter'
        ]
      },
      bathrooms: {
        label: 'Bathrooms',
        icon: '🚿',
        items: [
          'Everything in a deep clean, PLUS:',
          'Clean inside all cabinets and drawers',
          'Clean inside medicine cabinet',
          'Detail around all fixtures',
          'Polish all hardware and fixtures',
          'Thorough scrub of all tile and grout'
        ]
      },
      bedrooms_closets: {
        label: 'Bedrooms & Closets',
        icon: '🚪',
        items: [
          'Everything in a deep clean, PLUS:',
          'Clean inside all closets (shelves, rods, walls)',
          'Clean closet doors and tracks',
          'Wipe down all built-in shelving',
          'Vacuum all closet floors'
        ]
      },
      general: {
        label: 'Throughout the Home',
        icon: '🏠',
        items: [
          'Everything in a deep clean scope, PLUS:',
          'Clean inside all closets',
          'Clean garage entry area (if applicable)',
          'Clean laundry room and hookup area',
          'Wipe down all built-in shelving',
          'Clean accessible window tracks',
          'Final walkthrough inspection'
        ]
      }
    },
    notIncluded: [
      'Carpet shampooing or steam cleaning',
      'Wall washing or scuff removal',
      'Exterior windows',
      'Garage deep clean',
      'Moving or removing items left behind',
      'Post-construction or heavy debris cleanup',
      'Taking down books or removing items from shelving',
      'Surfaces not reachable with a 6-foot duster or 3-step ladder',
      'Animal urine, feces, or human waste (may incur additional costs)',
      'Smoke residue or nicotine buildup (may incur additional costs)',
      'Hazardous materials, needles, or biohazards',
      'Homes without running water, electricity, or HVAC between 64°F–75°F'
    ]
  }
};

/**
 * Products and chemicals used by Two Sisters Cleaning.
 * Grouped by category for the public Scope of Service page.
 */
export const cleaningProducts = [
  {
    category: 'Everyday Cleaners',
    icon: '🧴',
    items: [
      { name: 'Custom Glass & Mirror Cleaner', description: '70% isopropyl alcohol mixed in-house with essential oils and Dawn — for glass, windows, mirrors, and shiny surfaces' },
      { name: 'Dawn Dish Soap', description: 'Trusted all-purpose degreaser and gentle surface cleaner' },
      { name: 'Simple Green (Diluted)', description: 'Diluted all-purpose cleaner for general surfaces' },
      { name: 'Simple Green Degreaser (Light Dilution)', description: 'Less diluted for tougher grease and buildup' },
      { name: 'Citrus Degreaser', description: 'Heavy-duty degreaser for stubborn kitchen grease and grime' },
    ]
  },
  {
    category: 'Specialty & Deep Clean',
    icon: '✨',
    items: [
      { name: 'The Pink Stuff', description: 'Gentle abrasive paste for sinks, stovetops, and tough stains' },
      { name: 'Magic Erasers', description: 'Melamine sponges for scuffs, marks, and buildup on hard surfaces' },
      { name: 'Zep Tub & Tile Cleaner', description: 'Used when needed for heavy bathroom buildup' },
      { name: 'Zep Grout Cleaner', description: 'Used when needed for deep grout cleaning' },
    ]
  },
  {
    category: 'Floors',
    icon: '🪣',
    items: [
      { name: 'OdoBan Floor Cleaner', description: 'pH-neutral floor cleaner safe for all sealed hard floors' },
      { name: 'Bona Floor Cleaner', description: 'Used when requested by the client — ideal for hardwood floors' },
    ]
  },
  {
    category: 'Polish & Protect',
    icon: '🐝',
    items: [
      { name: "Bee's Wax Polish", description: 'Natural beeswax for polishing wood, glass, and countertops', link: 'https://beeswaxpolish.com/products/' },
    ]
  }
];

/**
 * Get the scope type key for a given cleaning frequency.
 * Maps estimate frequency values to the corresponding scope key.
 */
export function getScopeTypeForFrequency(frequency) {
  switch (frequency) {
    case 'weekly':
    case 'biweekly':
    case 'monthly':
      return 'recurring';
    case 'deep_clean':
    case 'one_time':
      return 'deep_clean';
    case 'move_in':
    case 'move_out':
    case 'move_in_out':
      return 'move_in_out';
    default:
      return 'recurring';
  }
}

// Sample data for dev/demo
export const sampleEstimates = [];
